import { useState, useEffect } from "react";
import { Typography, useTheme } from "@mui/material";
import Model from "../base/Model/ui";
import { Card, Col, Row } from "../base/Flex/ui";
import { DNA_CLASS_CONFIG_MAP, DNAClass } from "pages/cabal-id/dna/dnaConfig";
import BasicButton from "../Buttons/BasicButton";
import CheckBox from "../base/CheckBox/ui";

const FilterByClassModal = ({
  open,
  onClose,
  selectedClasses,
  setSelectedClasses,
}: {
  open: boolean;
  onClose: () => void;
  selectedClasses: Map<DNAClass, boolean>;
  setSelectedClasses: (selectedClasses: Map<DNAClass, boolean>) => void;
}) => {
  const [tempSelectedClasses, setTempSelectedClasses] = useState<Map<DNAClass, boolean>>(
    new Map(selectedClasses),
  );

  useEffect(() => {
    setTempSelectedClasses(new Map(selectedClasses));
  }, [selectedClasses]);

  const handleApplySelectedClasses = () => {
    setSelectedClasses(new Map(tempSelectedClasses));
    localStorage.setItem(
      "selectedClasses",
      JSON.stringify(Array.from(tempSelectedClasses.entries())),
    );
    onClose();
  };

  const handleClose = () => {
    setSelectedClasses(new Map());
    onClose();
  };

  return (
    <Model.Wrapper open={open} onClose={handleClose}>
      <Model.Container size="sm">
        <Model.Header.Wrapper>
          <Model.Header.Title>
            <Typography variant="mobile-h3">Filter by classes</Typography>
          </Model.Header.Title>
          <Model.Header.CloseButton close={handleClose} />
        </Model.Header.Wrapper>
        <Model.Content>
          <Row type="cc" fullWidth>
            <Col sx={{ padding: "32px 0", gap: "12px" }} fullWidth fullHeight>
              {Object.keys(DNA_CLASS_CONFIG_MAP).map((name) => {
                return (
                  <ClassOption
                    key={name}
                    name={name as DNAClass}
                    tempSelectedClasses={tempSelectedClasses}
                    setTempSelectedClasses={setTempSelectedClasses}
                  />
                );
              })}
            </Col>
          </Row>
          <Row fullWidth>
            <BasicButton
              variant="white"
              sx={{ width: "100%" }}
              onClick={handleApplySelectedClasses}>
              <Typography variant="mobile-action-s-heavy">Apply</Typography>
            </BasicButton>
          </Row>
        </Model.Content>
      </Model.Container>
    </Model.Wrapper>
  );
};

const ClassOption = ({
  name,
  tempSelectedClasses,
  setTempSelectedClasses,
}: {
  name: DNAClass;
  tempSelectedClasses: Map<DNAClass, boolean>;
  setTempSelectedClasses: (classes: Map<DNAClass, boolean>) => void;
}) => {
  const theme = useTheme();

  /* Blacklisted classes */
  if (name === "KING_OF_THE_SHILL") {
    return null;
  }

  const isSelected = tempSelectedClasses.get(name as DNAClass);

  const dnaClassConfig = DNA_CLASS_CONFIG_MAP[name as DNAClass]!;

  const handleSelect = () => {
    if (isSelected) {
      tempSelectedClasses.delete(name as DNAClass);
    } else {
      tempSelectedClasses.set(name as DNAClass, true);
    }
    setTempSelectedClasses(new Map(tempSelectedClasses));
  };

  return (
    <Row type="sc" gap="16px" fullWidth>
      <CheckBox isSelected={!!isSelected} handleSelect={handleSelect} />
      <Card.Item
        sx={{
          width: "100%",
          border: isSelected
            ? `1px solid ${theme.palette.border.light}`
            : `1px solid ${theme.palette.surface.silver}`,
        }}>
        <Col type="cs" gap="6px" section="description" fullWidth>
          <Row type="sc" fullWidth>
            <Typography variant="mobile-body-s-heavy">{dnaClassConfig.name}</Typography>
          </Row>
          <Typography variant="mobile-body-xs" color="text.secondary">
            {dnaClassConfig.subtitle}
          </Typography>
        </Col>
      </Card.Item>
    </Row>
  );
};

export default FilterByClassModal;
